/*
Copyright 2026 New Vector Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

export interface ColorThemePickerItemData {
  name: string;
  var: string;
  description?: string;
  defaultValue?: string;
  inputType?: "color" | "text";
}

export const cpdColorGroups = {
  text: [
    { name: "action-accent", var: "--cpd-color-text-action-accent" },
    { name: "action-primary", var: "--cpd-color-text-action-primary" },
    { name: "badge-accent", var: "--cpd-color-text-badge-accent" },
    { name: "badge-info", var: "--cpd-color-text-badge-info" },
    { name: "critical-primary", var: "--cpd-color-text-critical-primary" },
    { name: "decorative-1", var: "--cpd-color-text-decorative-1" },
    { name: "decorative-2", var: "--cpd-color-text-decorative-2" },
    { name: "decorative-3", var: "--cpd-color-text-decorative-3" },
    { name: "decorative-4", var: "--cpd-color-text-decorative-4" },
    { name: "decorative-5", var: "--cpd-color-text-decorative-5" },
    { name: "decorative-6", var: "--cpd-color-text-decorative-6" },
    { name: "disabled", var: "--cpd-color-text-disabled" },
    { name: "info-primary", var: "--cpd-color-text-info-primary" },
    { name: "link-external", var: "--cpd-color-text-link-external" },
    { name: "on-solid-primary", var: "--cpd-color-text-on-solid-primary" },
    { name: "placeholder", var: "--cpd-color-text-placeholder" },
    { name: "primary", var: "--cpd-color-text-primary" },
    { name: "secondary", var: "--cpd-color-text-secondary" },
    { name: "success-primary", var: "--cpd-color-text-success-primary" },
    { name: "theme-primary", var: "--cpd-color-theme-primary" },
  ],
  bg: [
    { name: "accent-hovered", var: "--cpd-color-bg-accent-hovered" },
    { name: "accent-pressed", var: "--cpd-color-bg-accent-pressed" },
    { name: "accent-rest", var: "--cpd-color-bg-accent-rest" },
    { name: "accent-selected", var: "--cpd-color-bg-accent-selected" },
    {
      name: "action-primary-disabled",
      var: "--cpd-color-bg-action-primary-disabled",
    },
    {
      name: "action-primary-hovered",
      var: "--cpd-color-bg-action-primary-hovered",
    },
    {
      name: "action-primary-pressed",
      var: "--cpd-color-bg-action-primary-pressed",
    },
    { name: "action-primary-rest", var: "--cpd-color-bg-action-primary-rest" },
    {
      name: "action-secondary-hovered",
      var: "--cpd-color-bg-action-secondary-hovered",
    },
    {
      name: "action-secondary-pressed",
      var: "--cpd-color-bg-action-secondary-pressed",
    },
    {
      name: "action-secondary-rest",
      var: "--cpd-color-bg-action-secondary-rest",
    },
    { name: "badge-accent", var: "--cpd-color-bg-badge-accent" },
    { name: "badge-default", var: "--cpd-color-bg-badge-default" },
    { name: "badge-info", var: "--cpd-color-bg-badge-info" },
    { name: "canvas-default", var: "--cpd-color-bg-canvas-default" },
    {
      name: "canvas-default-level-1",
      var: "--cpd-color-bg-canvas-default-level-1",
    },
    { name: "canvas-disabled", var: "--cpd-color-bg-canvas-disabled" },
    { name: "critical-hovered", var: "--cpd-color-bg-critical-hovered" },
    { name: "critical-primary", var: "--cpd-color-bg-critical-primary" },
    { name: "critical-subtle", var: "--cpd-color-bg-critical-subtle" },
    {
      name: "critical-subtle-hovered",
      var: "--cpd-color-bg-critical-subtle-hovered",
    },
    { name: "decorative-1", var: "--cpd-color-bg-decorative-1" },
    { name: "decorative-2", var: "--cpd-color-bg-decorative-2" },
    { name: "decorative-3", var: "--cpd-color-bg-decorative-3" },
    { name: "decorative-4", var: "--cpd-color-bg-decorative-4" },
    { name: "decorative-5", var: "--cpd-color-bg-decorative-5" },
    { name: "decorative-6", var: "--cpd-color-bg-decorative-6" },
    { name: "info-subtle", var: "--cpd-color-bg-info-subtle" },
    { name: "subtle-primary", var: "--cpd-color-bg-subtle-primary" },
    { name: "subtle-secondary", var: "--cpd-color-bg-subtle-secondary" },
    {
      name: "subtle-secondary-level-0",
      var: "--cpd-color-bg-subtle-secondary-level-0",
    },
    { name: "success-subtle", var: "--cpd-color-bg-success-subtle" },
  ],
  border: [
    { name: "accent-subtle", var: "--cpd-color-border-accent-subtle" },
    { name: "critical-hovered", var: "--cpd-color-border-critical-hovered" },
    { name: "critical-primary", var: "--cpd-color-border-critical-primary" },
    { name: "critical-subtle", var: "--cpd-color-border-critical-subtle" },
    { name: "disabled", var: "--cpd-color-border-disabled" },
    { name: "focused", var: "--cpd-color-border-focused" },
    { name: "info-subtle", var: "--cpd-color-border-info-subtle" },
    {
      name: "interactive-primary",
      var: "--cpd-color-border-interactive-primary",
    },
    {
      name: "interactive-secondary",
      var: "--cpd-color-border-interactive-secondary",
    },
    {
      name: "interactive-hovered",
      var: "--cpd-color-border-interactive-hovered",
    },
    { name: "success-subtle", var: "--cpd-color-border-success-subtle" },
  ],
  icon: [
    { name: "accent-primary", var: "--cpd-color-icon-accent-primary" },
    { name: "accent-tertiary", var: "--cpd-color-icon-accent-tertiary" },
    { name: "critical-primary", var: "--cpd-color-icon-critical-primary" },
    { name: "disabled", var: "--cpd-color-icon-disabled" },
    { name: "info-primary", var: "--cpd-color-icon-info-primary" },
    { name: "on-solid-primary", var: "--cpd-color-icon-on-solid-primary" },
    { name: "primary", var: "--cpd-color-icon-primary" },
    { name: "primary-alpha", var: "--cpd-color-icon-primary-alpha" },
    { name: "quaternary", var: "--cpd-color-icon-quaternary" },
    { name: "quaternary-alpha", var: "--cpd-color-icon-quaternary-alpha" },
    { name: "secondary", var: "--cpd-color-icon-secondary" },
    { name: "secondary-alpha", var: "--cpd-color-icon-secondary-alpha" },
    { name: "success-primary", var: "--cpd-color-icon-success-primary" },
    { name: "tertiary", var: "--cpd-color-icon-tertiary" },
    { name: "tertiary-alpha", var: "--cpd-color-icon-tertiary-alpha" },
  ],
  palette: [
    { name: "alpha-blue-100", var: "--cpd-color-alpha-blue-100" },
    { name: "alpha-blue-200", var: "--cpd-color-alpha-blue-200" },
    { name: "alpha-blue-300", var: "--cpd-color-alpha-blue-300" },
    { name: "alpha-blue-400", var: "--cpd-color-alpha-blue-400" },
    { name: "alpha-blue-500", var: "--cpd-color-alpha-blue-500" },
    { name: "alpha-blue-600", var: "--cpd-color-alpha-blue-600" },
    { name: "alpha-blue-700", var: "--cpd-color-alpha-blue-700" },
    { name: "alpha-blue-800", var: "--cpd-color-alpha-blue-800" },
    { name: "alpha-blue-900", var: "--cpd-color-alpha-blue-900" },
    { name: "alpha-blue-1000", var: "--cpd-color-alpha-blue-1000" },
    { name: "alpha-blue-1100", var: "--cpd-color-alpha-blue-1100" },
    { name: "alpha-blue-1200", var: "--cpd-color-alpha-blue-1200" },
    { name: "alpha-blue-1300", var: "--cpd-color-alpha-blue-1300" },
    { name: "alpha-blue-1400", var: "--cpd-color-alpha-blue-1400" },
    { name: "alpha-cyan-100", var: "--cpd-color-alpha-cyan-100" },
    { name: "alpha-cyan-200", var: "--cpd-color-alpha-cyan-200" },
    { name: "alpha-cyan-300", var: "--cpd-color-alpha-cyan-300" },
    { name: "alpha-cyan-400", var: "--cpd-color-alpha-cyan-400" },
    { name: "alpha-cyan-500", var: "--cpd-color-alpha-cyan-500" },
    { name: "alpha-cyan-600", var: "--cpd-color-alpha-cyan-600" },
    { name: "alpha-cyan-700", var: "--cpd-color-alpha-cyan-700" },
    { name: "alpha-cyan-800", var: "--cpd-color-alpha-cyan-800" },
    { name: "alpha-cyan-900", var: "--cpd-color-alpha-cyan-900" },
    { name: "alpha-cyan-1000", var: "--cpd-color-alpha-cyan-1000" },
    { name: "alpha-cyan-1100", var: "--cpd-color-alpha-cyan-1100" },
    { name: "alpha-cyan-1200", var: "--cpd-color-alpha-cyan-1200" },
    { name: "alpha-cyan-1300", var: "--cpd-color-alpha-cyan-1300" },
    { name: "alpha-cyan-1400", var: "--cpd-color-alpha-cyan-1400" },
    { name: "alpha-fuchsia-100", var: "--cpd-color-alpha-fuchsia-100" },
    { name: "alpha-fuchsia-200", var: "--cpd-color-alpha-fuchsia-200" },
    { name: "alpha-fuchsia-300", var: "--cpd-color-alpha-fuchsia-300" },
    { name: "alpha-fuchsia-400", var: "--cpd-color-alpha-fuchsia-400" },
    { name: "alpha-fuchsia-500", var: "--cpd-color-alpha-fuchsia-500" },
    { name: "alpha-fuchsia-600", var: "--cpd-color-alpha-fuchsia-600" },
    { name: "alpha-fuchsia-700", var: "--cpd-color-alpha-fuchsia-700" },
    { name: "alpha-fuchsia-800", var: "--cpd-color-alpha-fuchsia-800" },
    { name: "alpha-fuchsia-900", var: "--cpd-color-alpha-fuchsia-900" },
    { name: "alpha-fuchsia-1000", var: "--cpd-color-alpha-fuchsia-1000" },
    { name: "alpha-fuchsia-1100", var: "--cpd-color-alpha-fuchsia-1100" },
    { name: "alpha-fuchsia-1200", var: "--cpd-color-alpha-fuchsia-1200" },
    { name: "alpha-fuchsia-1300", var: "--cpd-color-alpha-fuchsia-1300" },
    { name: "alpha-fuchsia-1400", var: "--cpd-color-alpha-fuchsia-1400" },
    { name: "alpha-gray-100", var: "--cpd-color-alpha-gray-100" },
    { name: "alpha-gray-200", var: "--cpd-color-alpha-gray-200" },
    { name: "alpha-gray-300", var: "--cpd-color-alpha-gray-300" },
    { name: "alpha-gray-400", var: "--cpd-color-alpha-gray-400" },
    { name: "alpha-gray-500", var: "--cpd-color-alpha-gray-500" },
    { name: "alpha-gray-600", var: "--cpd-color-alpha-gray-600" },
    { name: "alpha-gray-700", var: "--cpd-color-alpha-gray-700" },
    { name: "alpha-gray-800", var: "--cpd-color-alpha-gray-800" },
    { name: "alpha-gray-900", var: "--cpd-color-alpha-gray-900" },
    { name: "alpha-gray-1000", var: "--cpd-color-alpha-gray-1000" },
    { name: "alpha-gray-1100", var: "--cpd-color-alpha-gray-1100" },
    { name: "alpha-gray-1200", var: "--cpd-color-alpha-gray-1200" },
    { name: "alpha-gray-1300", var: "--cpd-color-alpha-gray-1300" },
    { name: "alpha-gray-1400", var: "--cpd-color-alpha-gray-1400" },
    { name: "alpha-green-100", var: "--cpd-color-alpha-green-100" },
    { name: "alpha-green-200", var: "--cpd-color-alpha-green-200" },
    { name: "alpha-green-300", var: "--cpd-color-alpha-green-300" },
    { name: "alpha-green-400", var: "--cpd-color-alpha-green-400" },
    { name: "alpha-green-500", var: "--cpd-color-alpha-green-500" },
    { name: "alpha-green-600", var: "--cpd-color-alpha-green-600" },
    { name: "alpha-green-700", var: "--cpd-color-alpha-green-700" },
    { name: "alpha-green-800", var: "--cpd-color-alpha-green-800" },
    { name: "alpha-green-900", var: "--cpd-color-alpha-green-900" },
    { name: "alpha-green-1000", var: "--cpd-color-alpha-green-1000" },
    { name: "alpha-green-1100", var: "--cpd-color-alpha-green-1100" },
    { name: "alpha-green-1200", var: "--cpd-color-alpha-green-1200" },
    { name: "alpha-green-1300", var: "--cpd-color-alpha-green-1300" },
    { name: "alpha-green-1400", var: "--cpd-color-alpha-green-1400" },
    { name: "alpha-lime-100", var: "--cpd-color-alpha-lime-100" },
    { name: "alpha-lime-200", var: "--cpd-color-alpha-lime-200" },
    { name: "alpha-lime-300", var: "--cpd-color-alpha-lime-300" },
    { name: "alpha-lime-400", var: "--cpd-color-alpha-lime-400" },
    { name: "alpha-lime-500", var: "--cpd-color-alpha-lime-500" },
    { name: "alpha-lime-600", var: "--cpd-color-alpha-lime-600" },
    { name: "alpha-lime-700", var: "--cpd-color-alpha-lime-700" },
    { name: "alpha-lime-800", var: "--cpd-color-alpha-lime-800" },
    { name: "alpha-lime-900", var: "--cpd-color-alpha-lime-900" },
    { name: "alpha-lime-1000", var: "--cpd-color-alpha-lime-1000" },
    { name: "alpha-lime-1100", var: "--cpd-color-alpha-lime-1100" },
    { name: "alpha-lime-1200", var: "--cpd-color-alpha-lime-1200" },
    { name: "alpha-lime-1300", var: "--cpd-color-alpha-lime-1300" },
    { name: "alpha-lime-1400", var: "--cpd-color-alpha-lime-1400" },
    { name: "alpha-orange-100", var: "--cpd-color-alpha-orange-100" },
    { name: "alpha-orange-200", var: "--cpd-color-alpha-orange-200" },
    { name: "alpha-orange-300", var: "--cpd-color-alpha-orange-300" },
    { name: "alpha-orange-400", var: "--cpd-color-alpha-orange-400" },
    { name: "alpha-orange-500", var: "--cpd-color-alpha-orange-500" },
    { name: "alpha-orange-600", var: "--cpd-color-alpha-orange-600" },
    { name: "alpha-orange-700", var: "--cpd-color-alpha-orange-700" },
    { name: "alpha-orange-800", var: "--cpd-color-alpha-orange-800" },
    { name: "alpha-orange-900", var: "--cpd-color-alpha-orange-900" },
    { name: "alpha-orange-1000", var: "--cpd-color-alpha-orange-1000" },
    { name: "alpha-orange-1100", var: "--cpd-color-alpha-orange-1100" },
    { name: "alpha-orange-1200", var: "--cpd-color-alpha-orange-1200" },
    { name: "alpha-orange-1300", var: "--cpd-color-alpha-orange-1300" },
    { name: "alpha-orange-1400", var: "--cpd-color-alpha-orange-1400" },
    { name: "alpha-pink-100", var: "--cpd-color-alpha-pink-100" },
    { name: "alpha-pink-200", var: "--cpd-color-alpha-pink-200" },
    { name: "alpha-pink-300", var: "--cpd-color-alpha-pink-300" },
    { name: "alpha-pink-400", var: "--cpd-color-alpha-pink-400" },
    { name: "alpha-pink-500", var: "--cpd-color-alpha-pink-500" },
    { name: "alpha-pink-600", var: "--cpd-color-alpha-pink-600" },
    { name: "alpha-pink-700", var: "--cpd-color-alpha-pink-700" },
    { name: "alpha-pink-800", var: "--cpd-color-alpha-pink-800" },
    { name: "alpha-pink-900", var: "--cpd-color-alpha-pink-900" },
    { name: "alpha-pink-1000", var: "--cpd-color-alpha-pink-1000" },
    { name: "alpha-pink-1100", var: "--cpd-color-alpha-pink-1100" },
    { name: "alpha-pink-1200", var: "--cpd-color-alpha-pink-1200" },
    { name: "alpha-pink-1300", var: "--cpd-color-alpha-pink-1300" },
    { name: "alpha-pink-1400", var: "--cpd-color-alpha-pink-1400" },
    { name: "alpha-purple-100", var: "--cpd-color-alpha-purple-100" },
    { name: "alpha-purple-200", var: "--cpd-color-alpha-purple-200" },
    { name: "alpha-purple-300", var: "--cpd-color-alpha-purple-300" },
    { name: "alpha-purple-400", var: "--cpd-color-alpha-purple-400" },
    { name: "alpha-purple-500", var: "--cpd-color-alpha-purple-500" },
    { name: "alpha-purple-600", var: "--cpd-color-alpha-purple-600" },
    { name: "alpha-purple-700", var: "--cpd-color-alpha-purple-700" },
    { name: "alpha-purple-800", var: "--cpd-color-alpha-purple-800" },
    { name: "alpha-purple-900", var: "--cpd-color-alpha-purple-900" },
    { name: "alpha-purple-1000", var: "--cpd-color-alpha-purple-1000" },
    { name: "alpha-purple-1100", var: "--cpd-color-alpha-purple-1100" },
    { name: "alpha-purple-1200", var: "--cpd-color-alpha-purple-1200" },
    { name: "alpha-purple-1300", var: "--cpd-color-alpha-purple-1300" },
    { name: "alpha-purple-1400", var: "--cpd-color-alpha-purple-1400" },
    { name: "alpha-red-100", var: "--cpd-color-alpha-red-100" },
    { name: "alpha-red-200", var: "--cpd-color-alpha-red-200" },
    { name: "alpha-red-300", var: "--cpd-color-alpha-red-300" },
    { name: "alpha-red-400", var: "--cpd-color-alpha-red-400" },
    { name: "alpha-red-500", var: "--cpd-color-alpha-red-500" },
    { name: "alpha-red-600", var: "--cpd-color-alpha-red-600" },
    { name: "alpha-red-700", var: "--cpd-color-alpha-red-700" },
    { name: "alpha-red-800", var: "--cpd-color-alpha-red-800" },
    { name: "alpha-red-900", var: "--cpd-color-alpha-red-900" },
    { name: "alpha-red-1000", var: "--cpd-color-alpha-red-1000" },
    { name: "alpha-red-1100", var: "--cpd-color-alpha-red-1100" },
    { name: "alpha-red-1200", var: "--cpd-color-alpha-red-1200" },
    { name: "alpha-red-1300", var: "--cpd-color-alpha-red-1300" },
    { name: "alpha-red-1400", var: "--cpd-color-alpha-red-1400" },
    { name: "alpha-yellow-100", var: "--cpd-color-alpha-yellow-100" },
    { name: "alpha-yellow-200", var: "--cpd-color-alpha-yellow-200" },
    { name: "alpha-yellow-300", var: "--cpd-color-alpha-yellow-300" },
    { name: "alpha-yellow-400", var: "--cpd-color-alpha-yellow-400" },
    { name: "alpha-yellow-500", var: "--cpd-color-alpha-yellow-500" },
    { name: "alpha-yellow-600", var: "--cpd-color-alpha-yellow-600" },
    { name: "alpha-yellow-700", var: "--cpd-color-alpha-yellow-700" },
    { name: "alpha-yellow-800", var: "--cpd-color-alpha-yellow-800" },
    { name: "alpha-yellow-900", var: "--cpd-color-alpha-yellow-900" },
    { name: "alpha-yellow-1000", var: "--cpd-color-alpha-yellow-1000" },
    { name: "alpha-yellow-1100", var: "--cpd-color-alpha-yellow-1100" },
    { name: "alpha-yellow-1200", var: "--cpd-color-alpha-yellow-1200" },
    { name: "alpha-yellow-1300", var: "--cpd-color-alpha-yellow-1300" },
    { name: "alpha-yellow-1400", var: "--cpd-color-alpha-yellow-1400" },
    { name: "blue-100", var: "--cpd-color-blue-100" },
    { name: "blue-200", var: "--cpd-color-blue-200" },
    { name: "blue-300", var: "--cpd-color-blue-300" },
    { name: "blue-400", var: "--cpd-color-blue-400" },
    { name: "blue-500", var: "--cpd-color-blue-500" },
    { name: "blue-600", var: "--cpd-color-blue-600" },
    { name: "blue-700", var: "--cpd-color-blue-700" },
    { name: "blue-800", var: "--cpd-color-blue-800" },
    { name: "blue-900", var: "--cpd-color-blue-900" },
    { name: "blue-1000", var: "--cpd-color-blue-1000" },
    { name: "blue-1100", var: "--cpd-color-blue-1100" },
    { name: "blue-1200", var: "--cpd-color-blue-1200" },
    { name: "blue-1300", var: "--cpd-color-blue-1300" },
    { name: "blue-1400", var: "--cpd-color-blue-1400" },
    { name: "cyan-100", var: "--cpd-color-cyan-100" },
    { name: "cyan-200", var: "--cpd-color-cyan-200" },
    { name: "cyan-300", var: "--cpd-color-cyan-300" },
    { name: "cyan-400", var: "--cpd-color-cyan-400" },
    { name: "cyan-500", var: "--cpd-color-cyan-500" },
    { name: "cyan-600", var: "--cpd-color-cyan-600" },
    { name: "cyan-700", var: "--cpd-color-cyan-700" },
    { name: "cyan-800", var: "--cpd-color-cyan-800" },
    { name: "cyan-900", var: "--cpd-color-cyan-900" },
    { name: "cyan-1000", var: "--cpd-color-cyan-1000" },
    { name: "cyan-1100", var: "--cpd-color-cyan-1100" },
    { name: "cyan-1200", var: "--cpd-color-cyan-1200" },
    { name: "cyan-1300", var: "--cpd-color-cyan-1300" },
    { name: "cyan-1400", var: "--cpd-color-cyan-1400" },
    { name: "fuchsia-100", var: "--cpd-color-fuchsia-100" },
    { name: "fuchsia-200", var: "--cpd-color-fuchsia-200" },
    { name: "fuchsia-300", var: "--cpd-color-fuchsia-300" },
    { name: "fuchsia-400", var: "--cpd-color-fuchsia-400" },
    { name: "fuchsia-500", var: "--cpd-color-fuchsia-500" },
    { name: "fuchsia-600", var: "--cpd-color-fuchsia-600" },
    { name: "fuchsia-700", var: "--cpd-color-fuchsia-700" },
    { name: "fuchsia-800", var: "--cpd-color-fuchsia-800" },
    { name: "fuchsia-900", var: "--cpd-color-fuchsia-900" },
    { name: "fuchsia-1000", var: "--cpd-color-fuchsia-1000" },
    { name: "fuchsia-1100", var: "--cpd-color-fuchsia-1100" },
    { name: "fuchsia-1200", var: "--cpd-color-fuchsia-1200" },
    { name: "fuchsia-1300", var: "--cpd-color-fuchsia-1300" },
    { name: "fuchsia-1400", var: "--cpd-color-fuchsia-1400" },
    {
      name: "gradient-action-linear",
      var: "--cpd-color-gradient-action-linear",
      inputType: "text",
    },
    {
      name: "gradient-action-stop1",
      var: "--cpd-color-gradient-action-stop1",
      inputType: "text",
    },
    {
      name: "gradient-action-stop2",
      var: "--cpd-color-gradient-action-stop2",
      inputType: "text",
    },
    {
      name: "gradient-action-stop3",
      var: "--cpd-color-gradient-action-stop3",
      inputType: "text",
    },
    {
      name: "gradient-action-stop4",
      var: "--cpd-color-gradient-action-stop4",
      inputType: "text",
    },
    {
      name: "gradient-info-linear",
      var: "--cpd-color-gradient-info-linear",
      inputType: "text",
    },
    {
      name: "gradient-info-stop1",
      var: "--cpd-color-gradient-info-stop1",
      inputType: "text",
    },
    {
      name: "gradient-info-stop2",
      var: "--cpd-color-gradient-info-stop2",
      inputType: "text",
    },
    {
      name: "gradient-info-stop3",
      var: "--cpd-color-gradient-info-stop3",
      inputType: "text",
    },
    {
      name: "gradient-info-stop4",
      var: "--cpd-color-gradient-info-stop4",
      inputType: "text",
    },
    {
      name: "gradient-info-stop5",
      var: "--cpd-color-gradient-info-stop5",
      inputType: "text",
    },
    {
      name: "gradient-info-stop6",
      var: "--cpd-color-gradient-info-stop6",
      inputType: "text",
    },
    {
      name: "gradient-subtle-linear",
      var: "--cpd-color-gradient-subtle-linear",
      inputType: "text",
    },
    {
      name: "gradient-subtle-stop1",
      var: "--cpd-color-gradient-subtle-stop1",
      inputType: "text",
    },
    {
      name: "gradient-subtle-stop2",
      var: "--cpd-color-gradient-subtle-stop2",
      inputType: "text",
    },
    {
      name: "gradient-subtle-stop3",
      var: "--cpd-color-gradient-subtle-stop3",
      inputType: "text",
    },
    {
      name: "gradient-subtle-stop4",
      var: "--cpd-color-gradient-subtle-stop4",
      inputType: "text",
    },
    {
      name: "gradient-subtle-stop5",
      var: "--cpd-color-gradient-subtle-stop5",
      inputType: "text",
    },
    {
      name: "gradient-subtle-stop6",
      var: "--cpd-color-gradient-subtle-stop6",
      inputType: "text",
    },
    { name: "gray-100", var: "--cpd-color-gray-100" },
    { name: "gray-200", var: "--cpd-color-gray-200" },
    { name: "gray-300", var: "--cpd-color-gray-300" },
    { name: "gray-400", var: "--cpd-color-gray-400" },
    { name: "gray-500", var: "--cpd-color-gray-500" },
    { name: "gray-600", var: "--cpd-color-gray-600" },
    { name: "gray-700", var: "--cpd-color-gray-700" },
    { name: "gray-800", var: "--cpd-color-gray-800" },
    { name: "gray-900", var: "--cpd-color-gray-900" },
    { name: "gray-1000", var: "--cpd-color-gray-1000" },
    { name: "gray-1100", var: "--cpd-color-gray-1100" },
    { name: "gray-1200", var: "--cpd-color-gray-1200" },
    { name: "gray-1300", var: "--cpd-color-gray-1300" },
    { name: "gray-1400", var: "--cpd-color-gray-1400" },
    { name: "green-100", var: "--cpd-color-green-100" },
    { name: "green-200", var: "--cpd-color-green-200" },
    { name: "green-300", var: "--cpd-color-green-300" },
    { name: "green-400", var: "--cpd-color-green-400" },
    { name: "green-500", var: "--cpd-color-green-500" },
    { name: "green-600", var: "--cpd-color-green-600" },
    { name: "green-700", var: "--cpd-color-green-700" },
    { name: "green-800", var: "--cpd-color-green-800" },
    { name: "green-900", var: "--cpd-color-green-900" },
    { name: "green-1000", var: "--cpd-color-green-1000" },
    { name: "green-1100", var: "--cpd-color-green-1100" },
    { name: "green-1200", var: "--cpd-color-green-1200" },
    { name: "green-1300", var: "--cpd-color-green-1300" },
    { name: "green-1400", var: "--cpd-color-green-1400" },
    { name: "lime-100", var: "--cpd-color-lime-100" },
    { name: "lime-200", var: "--cpd-color-lime-200" },
    { name: "lime-300", var: "--cpd-color-lime-300" },
    { name: "lime-400", var: "--cpd-color-lime-400" },
    { name: "lime-500", var: "--cpd-color-lime-500" },
    { name: "lime-600", var: "--cpd-color-lime-600" },
    { name: "lime-700", var: "--cpd-color-lime-700" },
    { name: "lime-800", var: "--cpd-color-lime-800" },
    { name: "lime-900", var: "--cpd-color-lime-900" },
    { name: "lime-1000", var: "--cpd-color-lime-1000" },
    { name: "lime-1100", var: "--cpd-color-lime-1100" },
    { name: "lime-1200", var: "--cpd-color-lime-1200" },
    { name: "lime-1300", var: "--cpd-color-lime-1300" },
    { name: "lime-1400", var: "--cpd-color-lime-1400" },
    { name: "orange-100", var: "--cpd-color-orange-100" },
    { name: "orange-200", var: "--cpd-color-orange-200" },
    { name: "orange-300", var: "--cpd-color-orange-300" },
    { name: "orange-400", var: "--cpd-color-orange-400" },
    { name: "orange-500", var: "--cpd-color-orange-500" },
    { name: "orange-600", var: "--cpd-color-orange-600" },
    { name: "orange-700", var: "--cpd-color-orange-700" },
    { name: "orange-800", var: "--cpd-color-orange-800" },
    { name: "orange-900", var: "--cpd-color-orange-900" },
    { name: "orange-1000", var: "--cpd-color-orange-1000" },
    { name: "orange-1100", var: "--cpd-color-orange-1100" },
    { name: "orange-1200", var: "--cpd-color-orange-1200" },
    { name: "orange-1300", var: "--cpd-color-orange-1300" },
    { name: "orange-1400", var: "--cpd-color-orange-1400" },
    { name: "pink-100", var: "--cpd-color-pink-100" },
    { name: "pink-200", var: "--cpd-color-pink-200" },
    { name: "pink-300", var: "--cpd-color-pink-300" },
    { name: "pink-400", var: "--cpd-color-pink-400" },
    { name: "pink-500", var: "--cpd-color-pink-500" },
    { name: "pink-600", var: "--cpd-color-pink-600" },
    { name: "pink-700", var: "--cpd-color-pink-700" },
    { name: "pink-800", var: "--cpd-color-pink-800" },
    { name: "pink-900", var: "--cpd-color-pink-900" },
    { name: "pink-1000", var: "--cpd-color-pink-1000" },
    { name: "pink-1100", var: "--cpd-color-pink-1100" },
    { name: "pink-1200", var: "--cpd-color-pink-1200" },
    { name: "pink-1300", var: "--cpd-color-pink-1300" },
    { name: "pink-1400", var: "--cpd-color-pink-1400" },
    { name: "purple-100", var: "--cpd-color-purple-100" },
    { name: "purple-200", var: "--cpd-color-purple-200" },
    { name: "purple-300", var: "--cpd-color-purple-300" },
    { name: "purple-400", var: "--cpd-color-purple-400" },
    { name: "purple-500", var: "--cpd-color-purple-500" },
    { name: "purple-600", var: "--cpd-color-purple-600" },
    { name: "purple-700", var: "--cpd-color-purple-700" },
    { name: "purple-800", var: "--cpd-color-purple-800" },
    { name: "purple-900", var: "--cpd-color-purple-900" },
    { name: "purple-1000", var: "--cpd-color-purple-1000" },
    { name: "purple-1100", var: "--cpd-color-purple-1100" },
    { name: "purple-1200", var: "--cpd-color-purple-1200" },
    { name: "purple-1300", var: "--cpd-color-purple-1300" },
    { name: "purple-1400", var: "--cpd-color-purple-1400" },
    { name: "red-100", var: "--cpd-color-red-100" },
    { name: "red-200", var: "--cpd-color-red-200" },
    { name: "red-300", var: "--cpd-color-red-300" },
    { name: "red-400", var: "--cpd-color-red-400" },
    { name: "red-500", var: "--cpd-color-red-500" },
    { name: "red-600", var: "--cpd-color-red-600" },
    { name: "red-700", var: "--cpd-color-red-700" },
    { name: "red-800", var: "--cpd-color-red-800" },
    { name: "red-900", var: "--cpd-color-red-900" },
    { name: "red-1000", var: "--cpd-color-red-1000" },
    { name: "red-1100", var: "--cpd-color-red-1100" },
    { name: "red-1200", var: "--cpd-color-red-1200" },
    { name: "red-1300", var: "--cpd-color-red-1300" },
    { name: "red-1400", var: "--cpd-color-red-1400" },
    { name: "theme-bg", var: "--cpd-color-theme-bg" },
    {
      name: "transparent",
      var: "--cpd-color-transparent",
      inputType: "text",
    },
    { name: "yellow-100", var: "--cpd-color-yellow-100" },
    { name: "yellow-200", var: "--cpd-color-yellow-200" },
    { name: "yellow-300", var: "--cpd-color-yellow-300" },
    { name: "yellow-400", var: "--cpd-color-yellow-400" },
    { name: "yellow-500", var: "--cpd-color-yellow-500" },
    { name: "yellow-600", var: "--cpd-color-yellow-600" },
    { name: "yellow-700", var: "--cpd-color-yellow-700" },
    { name: "yellow-800", var: "--cpd-color-yellow-800" },
    { name: "yellow-900", var: "--cpd-color-yellow-900" },
    { name: "yellow-1000", var: "--cpd-color-yellow-1000" },
    { name: "yellow-1100", var: "--cpd-color-yellow-1100" },
    { name: "yellow-1200", var: "--cpd-color-yellow-1200" },
    { name: "yellow-1300", var: "--cpd-color-yellow-1300" },
    { name: "yellow-1400", var: "--cpd-color-yellow-1400" },
  ],
} as const satisfies Record<string, readonly ColorThemePickerItemData[]>;

const utilityVars: readonly ColorThemePickerItemData[] = [
  {
    name: "video-tile-background",
    var: "--video-tile-background",
    description:
      "Фон видеотайлов и превью камеры, когда видео ещё не отрисовано.",
  },
  {
    name: "stopgap-color-on-solid-accent",
    var: "--stopgap-color-on-solid-accent",
    description:
      "Цвет контента на акцентной сплошной заливке. Сейчас используется в старых transitional-элементах, например в чекбоксе.",
  },
  {
    name: "stopgap-background-85",
    var: "--stopgap-background-85",
    description:
      "Полупрозрачный светлый фон для transitional-слоёв. Это legacy stopgap-переменная между старыми и Compound-стилями.",
    inputType: "text",
  },
  {
    name: "small-drop-shadow-color",
    var: "--small-drop-shadow-color",
    description:
      "Цвет малой тени для плавающих элементов вроде toast, плиток и переключателей layout.",
    inputType: "text",
  },
  {
    name: "subtle-drop-shadow-color",
    var: "--subtle-drop-shadow-color",
    description:
      "Цвет более мягкой вспомогательной тени. Сейчас это базовый утилитарный токен на случай вторичных теней.",
    inputType: "text",
  },
  {
    name: "overlay-backdrop-background",
    var: "--overlay-backdrop-background",
    description:
      "Фон затемнения под модалками и оверлеями, который перекрывает весь экран.",
    inputType: "text",
  },
  {
    name: "reaction-indicator-background",
    var: "--reaction-indicator-background",
    description:
      "Полупрозрачный фон плашки реакции, которая всплывает поверх звонка.",
    inputType: "text",
  },
  {
    name: "call-overlay-foreground",
    var: "--call-overlay-foreground",
    description:
      "Цвет текста на тёмных плашках поверх звонка (реакция, таймер, подпись ожидания) — подложки всегда тёмные, поэтому не зависят от темы.",
    inputType: "text",
  },
  {
    name: "reaction-indicator-shadow-color",
    var: "--reaction-indicator-shadow-color",
    description:
      "Цвет тени у индикатора реакции, чтобы плашка читалась на фоне видео.",
    inputType: "text",
  },
  {
    name: "input-checkbox-border",
    var: "--input-checkbox-border",
    description: "Цвет рамки у кастомного чекбокса в неактивном состоянии.",
    inputType: "text",
  },
  {
    name: "waiting-pulse-border",
    var: "--waiting-pulse-border",
    description:
      "Цвет пульсирующего кольца в состоянии ожидания подключения/входа в звонок.",
    inputType: "text",
  },
  {
    name: "call-elapsed-timer-background",
    var: "--call-elapsed-timer-background",
    description: "Фон плашки таймера звонка в верхней части экрана.",
    inputType: "text",
  },
  {
    name: "grid-speaking-1-start",
    var: "--grid-tile-speaking-gradient-1-start",
    description:
      "Первый стартовый цвет внешнего speaking-градиента у тайла активного говорящего.",
    inputType: "text",
  },
  {
    name: "grid-speaking-1-end",
    var: "--grid-tile-speaking-gradient-1-end",
    description:
      "Первый конечный цвет внешнего speaking-градиента у тайла активного говорящего.",
    inputType: "text",
  },
  {
    name: "grid-speaking-2-start",
    var: "--grid-tile-speaking-gradient-2-start",
    description:
      "Второй стартовый цвет внутреннего speaking-градиента у тайла активного говорящего.",
    inputType: "text",
  },
  {
    name: "grid-speaking-2-end",
    var: "--grid-tile-speaking-gradient-2-end",
    description:
      "Второй конечный цвет внутреннего speaking-градиента у тайла активного говорящего.",
    inputType: "text",
  },
];

export const themeUtilityGroups = {
  light: utilityVars,
  dark: utilityVars,
} as const satisfies Record<
  "light" | "dark",
  readonly ColorThemePickerItemData[]
>;

export const allColorThemeVars = [
  ...Object.values(cpdColorGroups).flat(),
  ...themeUtilityGroups.light,
];
